// CompanyDetail — a single company's issuer identity + document config.
//
// Wired to GET /v1/companies/:id and PUT /v1/companies/:id. The form is tabbed
// (Identity / Taxation / Templates) inside a .card; Save runs a useMutation and
// invalidates the company + list queries. Template dropdowns are sourced from
// the GET /v1/templates registry, filtered by doc_type.
import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { ContactsPanel } from "@/components/ContactsPanel";
import { ThemePanel } from "@/components/ThemePanel";
import { TemplatesPanel } from "@/components/TemplatesPanel";
import { DocumentPreview } from "@/components/DocumentPreview";
import { Pill, Segmented } from "@/components/UI";
import { formatDateTime } from "@/utils/format";
import { apiErrorMessage } from "@/utils/apiError";
import { useCompany } from "@/queries/useCompany";
import { useTemplates } from "@/queries/useTemplates";
import { useUI } from "@/store/ui";
import {
  updateCompany,
  bpsToPct,
  pctToBps,
  companyWarnings,
  issuerErrors,
  issuerFieldError,
  issuingStatus,
  widgetProvenance,
  isValidBusinessProfileId,
  businessProfileIdHint,
  MAX_BUSINESS_PROFILE_ID_LEN,
  DOCUMENT_MODES,
  DOCUMENT_MODE_LABELS,
  TAXATIONS,
  TAXATION_LABELS,
  type Company,
  type CompanyWritePayload,
  type CompanyIssuer,
  type DocumentMode,
  type Taxation,
} from "@/api/companies";

type Tab =
  | "identity"
  | "documents"
  | "taxation"
  | "templates"
  | "branding"
  | "designer"
  | "delivery"
  | "people";

function toForm(c: Company): CompanyWritePayload {
  return {
    display_name: c.display_name,
    widget_business_profile_id: c.widget_business_profile_id,
    mcc: c.mcc ?? "",
    issuer: { ...c.issuer },
    document_mode: c.document_mode,
    taxation: c.taxation,
    default_gst_rate_bps: c.default_gst_rate_bps,
    invoice_template: c.invoice_template,
    receipt_template: c.receipt_template,
    // Sent on EVERY save. The API reads an absent key as "preserve" and "" as
    // "clear"; round-tripping them explicitly means what this form shows is
    // what gets stored, with no invisible third state.
    public_base_url: c.public_base_url ?? "",
    email_domain: c.email_domain ?? "",
    notification_brand: c.notification_brand ?? "",
    from_name: c.from_name ?? "",
    from_address: c.from_address ?? "",
    reply_to: c.reply_to ?? "",
  };
}

// A labelled text input bound to an issuer field.
function IssuerField({
  label,
  field,
  form,
  set,
  placeholder,
}: {
  label: string;
  field: keyof CompanyIssuer;
  form: CompanyWritePayload;
  set: (patch: Partial<CompanyIssuer>) => void;
  placeholder?: string;
}) {
  // Shown beside the field rather than left to the server's 400. The issuer
  // block is frozen onto every document at generation, so a malformed GSTIN is
  // permanent on everything already issued — this is the one place in the panel
  // where catching it before save is worth the duplicated grammar.
  const error = issuerFieldError(field, form.issuer[field]);
  return (
    <div className="field">
      <label className="label" htmlFor={`issuer-${field}`}>
        {label}
      </label>
      <input
        id={`issuer-${field}`}
        className="input"
        type="text"
        value={form.issuer[field] ?? ""}
        placeholder={placeholder}
        aria-invalid={error != null}
        aria-describedby={error ? `issuer-${field}-err` : undefined}
        onChange={(e) => set({ [field]: e.target.value })}
      />
      {error && (
        <div
          id={`issuer-${field}-err`}
          className="err"
          style={{ fontSize: 12 }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

export function CompanyDetail() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const addToast = useUI((s) => s.addToast);
  const { data: company, isLoading, isError } = useCompany(id);
  const { data: templates = [] } = useTemplates(id);

  const [tab, setTab] = useState<Tab>("identity");
  const [form, setForm] = useState<CompanyWritePayload | null>(null);
  // GST rate is edited as a percent string (e.g. "18", "2.5") but stored on the
  // wire as integer basis points. A dedicated string held here lets the
  // operator type intermediate values like "2." without losing keystrokes.
  const [gstPct, setGstPct] = useState("0");
  const [error, setError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Mirrors the server's rule that from_address must sit on email_domain.
  // Checked here so the operator sees WHY while typing, not as a 400 after
  // saving: mail sent from a domain the merchant is not attributed fails
  // SPF/DKIM and is indistinguishable from a spoof in their name.
  const fromAddressError = (() => {
    const addr = (form?.from_address ?? "").trim().toLowerCase();
    const domain = (form?.email_domain ?? "").trim().toLowerCase();
    if (addr === "") return null;
    const at = addr.lastIndexOf("@");
    if (at < 1 || at === addr.length - 1) return "Not a valid email address.";
    if (domain === "") return "Set an email domain first.";
    if (addr.slice(at + 1) !== domain)
      return `Must be on ${domain} — sending as a domain you are not attributed fails SPF/DKIM.`;
    return null;
  })();

  // Seed the form whenever the company (re)loads.
  useEffect(() => {
    if (company) {
      setForm(toForm(company));
      setGstPct(bpsToPct(company.default_gst_rate_bps));
    }
  }, [company]);

  const mutation = useMutation({
    mutationFn: (payload: CompanyWritePayload) => updateCompany(id!, payload),
    onSuccess: async (updated) => {
      setError(null);
      await qc.invalidateQueries({ queryKey: ["company", id] });
      await qc.invalidateQueries({ queryKey: ["companies"] });
      addToast({ title: `${updated.display_name} saved`, variant: "success" });
    },
    onError: (err) => {
      setError(apiErrorMessage(err, "Could not save the company. Try again."));
    },
  });

  function setIssuer(patch: Partial<CompanyIssuer>) {
    setForm((f) => (f ? { ...f, issuer: { ...f.issuer, ...patch } } : f));
  }
  function setField<K extends keyof CompanyWritePayload>(
    key: K,
    value: CompanyWritePayload[K],
  ) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form) return;
    // ── Blocking guardrails: switch to the offending tab, then block. ──────
    const name = form.display_name.trim();
    if (name === "") {
      setError("Display name can't be empty.");
      setTab("identity");
      return;
    }
    const bp = form.widget_business_profile_id?.trim() ?? "";
    if (bp !== "" && !isValidBusinessProfileId(bp)) {
      setError(
        "Collection profile can't contain spaces and must be under 200 characters. Paste the id exactly as widget shows it, or leave it blank.",
      );
      setTab("identity");
      return;
    }
    if (form.taxation === "gst" && form.default_gst_rate_bps === 0) {
      setError(
        "GST is selected but the rate is 0%. Set a GST rate, or choose 'No tax'.",
      );
      setTab("taxation");
      return;
    }
    // BLOCKING, not a warning, and this is the one identity check that earns
    // it. The issuer block is frozen onto every document at generation and
    // never re-resolved, so a malformed GSTIN is permanent on everything
    // already issued — there is no later save that repairs those documents.
    // go-pim refuses it too; this is so the operator learns at the field
    // instead of from a 400.
    const badIssuer = issuerErrors(form.issuer);
    if (badIssuer.length > 0) {
      setError(badIssuer[0]);
      setTab("identity");
      return;
    }
    mutation.mutate({
      ...form,
      display_name: name,
      widget_business_profile_id: bp || null,
    });
  }

  if (isLoading || !form) {
    return (
      <div className="page">
        <Link to="/companies" className="btn ghost sm">
          <I.ChevronLeft size={12} /> Back to companies
        </Link>
        <div className="empty" style={{ marginTop: 24 }}>
          <div className="dim" style={{ fontSize: 13 }}>
            {isError ? "Company not found." : "Loading company…"}
          </div>
        </div>
      </div>
    );
  }

  if (isError || !company) {
    return (
      <div className="page">
        <Link to="/companies" className="btn ghost sm">
          <I.ChevronLeft size={12} /> Back to companies
        </Link>
        <h1 style={{ marginTop: 16 }}>Company not found</h1>
        <p className="dim">
          No company with id {id}, or you don't have access.
        </p>
      </div>
    );
  }

  const invoiceTemplates = templates.filter((t) => t.doc_type === "invoice");
  const receiptTemplates = templates.filter((t) => t.doc_type === "receipt");

  // Derived from the LIVE form so the operator sees their config go live (or
  // fall dark) as they edit, before saving. Provenance comes from the SAVED
  // company, not the form — sync_state, widget_status and widget_document_mode
  // are all widget's, and nothing on this page can change them.
  //
  // widgetProvenance() rather than field-by-field: `form` is a
  // CompanyWritePayload, which by design carries only what an operator may
  // write, so every widget-owned input has to be threaded from the saved
  // company. Listing them by hand is how this page kept claiming "Issuing"
  // after the widget veto was added everywhere else — the helper makes a
  // missing field a compile error instead of a silently undefined one.
  const status = issuingStatus({ ...form, ...widgetProvenance(company) });
  const warnings = companyWarnings(form);

  // widget owns this company's identity, so the fields it owns are read-only.
  // 'absent' counts: widget has dropped the profile, but the row is still
  // widget's — letting an operator re-point it would fabricate a link widget
  // never made, and the next sweep would not agree.
  //
  // POSITIVE test, not `!== "local"`. This panel deploys independently of the
  // API, so against a backend that predates the mirror fields sync_state is
  // undefined — and a negative test would read that as widget-owned and lock
  // EVERY company's identity read-only. Absent evidence of widget ownership
  // means editable, which is the behaviour this page has always had.
  const widgetOwned =
    company.sync_state === "synced" || company.sync_state === "absent";
  const bpHint = businessProfileIdHint(form.widget_business_profile_id ?? "");

  return (
    <div className="page">
      <Link to="/companies" className="btn ghost sm">
        <I.ChevronLeft size={12} /> Back to companies
      </Link>
      <div className="page-head" style={{ marginTop: 12 }}>
        <div>
          <div className="eyebrow mono">{company.id}</div>
          <div className="row gap-12" style={{ alignItems: "center" }}>
            <h1>{form.display_name || company.display_name}</h1>
            <Pill tone={status.tone}>{status.label}</Pill>
          </div>
          <div className="sub">
            updated {formatDateTime(company.updated_at)}
          </div>
        </div>
        <div className="actions">
          {/* Preview mints NOTHING and burns no document number — see
              src/api/preview.ts. That is what makes it safe to click freely
              while configuring, which is exactly when it is useful. */}
          <button
            className="btn"
            type="button"
            onClick={() => setPreviewOpen(true)}
          >
            <I.Eye size={12} /> Preview
          </button>
          <Link className="btn" to={`/companies/${company.id}/products`}>
            <I.Wallet size={12} /> Products
          </Link>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card" style={{ padding: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <Segmented<Tab>
              value={tab}
              onChange={setTab}
              label="Company section"
              options={[
                { value: "identity", label: "Identity" },
                { value: "documents", label: "Documents" },
                { value: "taxation", label: "Taxation" },
                { value: "templates", label: "Templates" },
                { value: "branding", label: "Branding" },
                { value: "designer", label: "Designer" },
                { value: "delivery", label: "Delivery" },
                { value: "people", label: "People" },
              ]}
            />
          </div>

          {tab === "identity" && (
            <div className="stack-16">
              {widgetOwned && (
                // These fields belong to widget. An edit here SAVES and then
                // gets silently reverted by the next full-snapshot sweep, which
                // is worse than refusing it — the operator watches it succeed.
                <div
                  className="row gap-8"
                  style={{
                    fontSize: 12,
                    color: "var(--muted)",
                    alignItems: "flex-start",
                    lineHeight: 1.55,
                  }}
                >
                  <I.Lock size={12} style={{ marginTop: 2, flexShrink: 0 }} />
                  <span>
                    The name and collection profile below are owned by widget
                    and sync down from it, so they can't be edited here — the
                    next sync would overwrite the change. Everything else on
                    this page is yours, and no sync ever touches it. The name
                    that <strong>prints on a document</strong> is the issuer
                    legal name further down.
                  </span>
                </div>
              )}
              <div className="field">
                <label className="label" htmlFor="cd-name">
                  Display name
                </label>
                <input
                  id="cd-name"
                  className="input"
                  type="text"
                  value={form.display_name}
                  readOnly={widgetOwned}
                  disabled={widgetOwned}
                  onChange={(e) => setField("display_name", e.target.value)}
                />
              </div>
              <div className="field">
                <label className="label" htmlFor="cd-bp">
                  Collection profile
                </label>
                <input
                  id="cd-bp"
                  className="input mono"
                  type="text"
                  value={form.widget_business_profile_id ?? ""}
                  placeholder="bp_3fa85f64-5717-4562-b3fc-2c963f66afa6"
                  maxLength={MAX_BUSINESS_PROFILE_ID_LEN}
                  spellCheck={false}
                  readOnly={widgetOwned}
                  disabled={widgetOwned}
                  onChange={(e) =>
                    setField(
                      "widget_business_profile_id",
                      e.target.value === "" ? null : e.target.value,
                    )
                  }
                />
                <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                  {widgetOwned ? (
                    <>
                      Confirmed by widget
                      {company.widget_synced_at
                        ? ` · last synced ${formatDateTime(company.widget_synced_at)}`
                        : ""}
                      .
                    </>
                  ) : (
                    <>
                      The widget business profile whose payments this company
                      issues documents for. Paste it <strong>exactly</strong> as
                      widget shows it, prefix included — a link that doesn't
                      resolve issues nothing, and nothing will tell you.
                    </>
                  )}
                </div>
                {!widgetOwned && bpHint && (
                  <div
                    className="row gap-6"
                    style={{ color: "var(--warn)", fontSize: 11, marginTop: 4 }}
                  >
                    <I.AlertCircle size={12} />
                    <span>{bpHint}</span>
                  </div>
                )}
              </div>

              <div className="field" style={{ width: 200 }}>
                <label className="label" htmlFor="cd-mcc">
                  Merchant category (MCC) <span className="opt">optional</span>
                </label>
                <input
                  id="cd-mcc"
                  className="input mono"
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  value={form.mcc}
                  placeholder="5013"
                  onChange={(e) => setField("mcc", e.target.value)}
                />
                <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                  Four digits (ISO 18245). Kept as text because leading zeros
                  matter — 0742 is veterinary services, and 742 is a different
                  category entirely. widget presents this to the PSP, and it is
                  frozen onto each document as the code used for that payment.
                </div>
              </div>

              <div className="nav-section" style={{ marginTop: 4 }}>
                Issuer identity
              </div>
              <IssuerField
                label="Legal name"
                field="legal_name"
                form={form}
                set={setIssuer}
              />
              <IssuerField
                label="Address"
                field="address"
                form={form}
                set={setIssuer}
              />
              <div className="row gap-12">
                <div style={{ flex: 1 }}>
                  <IssuerField
                    label="GSTIN"
                    field="gstin"
                    form={form}
                    set={setIssuer}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <IssuerField
                    label="PAN"
                    field="pan"
                    form={form}
                    set={setIssuer}
                  />
                </div>
              </div>
              <div className="row gap-12">
                <div style={{ flex: 1 }}>
                  <IssuerField
                    label="CIN"
                    field="cin"
                    form={form}
                    set={setIssuer}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <IssuerField
                    label="State code"
                    field="state_code"
                    form={form}
                    set={setIssuer}
                    placeholder="e.g. 27"
                  />
                </div>
              </div>
              <IssuerField
                label="80G registration"
                field="reg_80g"
                form={form}
                set={setIssuer}
                placeholder="80G registration number (donations)"
              />
              <IssuerField
                label="Contact"
                field="contact"
                form={form}
                set={setIssuer}
              />
              <IssuerField
                label="Logo URL"
                field="logo_url"
                form={form}
                set={setIssuer}
                placeholder="https://…"
              />
              <div className="field">
                <label className="label" htmlFor="issuer-bank_details">
                  Bank details
                </label>
                <textarea
                  id="issuer-bank_details"
                  className="input"
                  rows={3}
                  value={form.issuer.bank_details ?? ""}
                  placeholder="Account name, number, IFSC, branch…"
                  onChange={(e) => setIssuer({ bank_details: e.target.value })}
                />
              </div>
            </div>
          )}

          {tab === "documents" && (
            <div className="stack-16">
              <div className="field">
                <label className="label" htmlFor="cd-mode">
                  Documents to issue
                </label>
                <select
                  id="cd-mode"
                  className="input"
                  value={form.document_mode}
                  onChange={(e) =>
                    setField("document_mode", e.target.value as DocumentMode)
                  }
                >
                  {DOCUMENT_MODES.map((m) => (
                    <option key={m} value={m}>
                      {DOCUMENT_MODE_LABELS[m]}
                    </option>
                  ))}
                </select>
                <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                  Which documents this company issues at settle time.
                </div>
              </div>
            </div>
          )}

          {tab === "taxation" && (
            <div className="stack-16">
              <div className="field">
                <label className="label" htmlFor="cd-tax">
                  Taxation
                </label>
                <select
                  id="cd-tax"
                  className="input"
                  value={form.taxation}
                  onChange={(e) =>
                    setField("taxation", e.target.value as Taxation)
                  }
                >
                  {TAXATIONS.map((t) => (
                    <option key={t} value={t}>
                      {TAXATION_LABELS[t]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label className="label" htmlFor="cd-gst">
                  GST rate (%)
                </label>
                <input
                  id="cd-gst"
                  className="input"
                  type="number"
                  min={0}
                  step="0.01"
                  value={gstPct}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setGstPct(raw);
                    const pct = parseFloat(raw);
                    setField(
                      "default_gst_rate_bps",
                      Number.isNaN(pct) ? 0 : pctToBps(Math.max(0, pct)),
                    );
                  }}
                />
                <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                  e.g. 18 for 18% GST.
                </div>
              </div>
            </div>
          )}

          {tab === "templates" && (
            <div className="stack-16">
              <div className="field">
                <label className="label" htmlFor="cd-inv">
                  Invoice template
                </label>
                <select
                  id="cd-inv"
                  className="input"
                  value={form.invoice_template}
                  onChange={(e) => setField("invoice_template", e.target.value)}
                >
                  <option value="">— none —</option>
                  {invoiceTemplates.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.title} ({t.key})
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label className="label" htmlFor="cd-rec">
                  Receipt template
                </label>
                <select
                  id="cd-rec"
                  className="input"
                  value={form.receipt_template}
                  onChange={(e) => setField("receipt_template", e.target.value)}
                >
                  <option value="">— none —</option>
                  {receiptTemplates.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.title} ({t.key})
                    </option>
                  ))}
                </select>
              </div>
              {templates.length === 0 && (
                <div className="dim" style={{ fontSize: 12 }}>
                  No templates registered (GET /v1/templates returned none).
                </div>
              )}
            </div>
          )}

          {tab === "delivery" && (
            <div className="stack-16">
              <div className="dim" style={{ fontSize: 12, lineHeight: 1.55 }}>
                Where this merchant's receipts live and how its email leaves.
                Leave everything blank to use the platform defaults — that is
                what every company does today, and it is a valid state.
              </div>

              <div className="nav-section" style={{ marginTop: 4 }}>
                Receipt link
              </div>
              <div className="field">
                <label className="label" htmlFor="cd-baseurl">
                  Public base URL
                </label>
                <input
                  id="cd-baseurl"
                  className="input"
                  type="text"
                  value={form.public_base_url}
                  placeholder="https://r.example.com"
                  onChange={(e) => setField("public_base_url", e.target.value)}
                />
                <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                  https only, host only — no path. Receipts link to{" "}
                  <span className="mono">
                    {(form.public_base_url || "\u2039default\u203a") +
                      "/r/\u2039slug\u203a"}
                  </span>
                  . Documents freeze the domain they were issued under, so
                  changing this never moves an existing receipt — and the old
                  host must keep serving <span className="mono">/r/</span> for
                  as long as any receipt points at it.
                </div>
              </div>

              <div className="nav-section" style={{ marginTop: 4 }}>
                Email sender
              </div>
              <div className="row gap-12">
                <div className="field" style={{ flex: 1 }}>
                  <label className="label" htmlFor="cd-emaildomain">
                    Email domain
                  </label>
                  <input
                    id="cd-emaildomain"
                    className="input"
                    type="text"
                    value={form.email_domain}
                    placeholder="example.com"
                    onChange={(e) => setField("email_domain", e.target.value)}
                  />
                  <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                    A hostname, not an address. Needs SPF and DKIM published, or
                    mail silently lands in spam.
                  </div>
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <label className="label" htmlFor="cd-brand">
                    Notification brand
                  </label>
                  <input
                    id="cd-brand"
                    className="input"
                    type="text"
                    value={form.notification_brand}
                    placeholder="e.g. vrf3"
                    onChange={(e) =>
                      setField("notification_brand", e.target.value)
                    }
                  />
                  <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                    Selects this merchant's email template and channel. Without
                    it there is no per-merchant template to edit.
                  </div>
                </div>
              </div>

              <div className="row gap-12">
                <div className="field" style={{ flex: 1 }}>
                  <label className="label" htmlFor="cd-fromname">
                    From name
                  </label>
                  <input
                    id="cd-fromname"
                    className="input"
                    type="text"
                    value={form.from_name}
                    placeholder="VRF3 Projects"
                    onChange={(e) => setField("from_name", e.target.value)}
                  />
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <label className="label" htmlFor="cd-fromaddr">
                    From address
                  </label>
                  <input
                    id="cd-fromaddr"
                    className="input"
                    type="text"
                    value={form.from_address}
                    aria-invalid={fromAddressError ? true : undefined}
                    placeholder={
                      form.email_domain
                        ? "noreply@" + form.email_domain
                        : "noreply@example.com"
                    }
                    onChange={(e) => setField("from_address", e.target.value)}
                  />
                  {fromAddressError ? (
                    <div className="field-error">{fromAddressError}</div>
                  ) : (
                    <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                      Must be on the email domain above.
                    </div>
                  )}
                </div>
              </div>

              <div className="field">
                <label className="label" htmlFor="cd-replyto">
                  Reply-to <span className="opt">optional</span>
                </label>
                <input
                  id="cd-replyto"
                  className="input"
                  type="text"
                  value={form.reply_to}
                  placeholder="support@example.com"
                  onChange={(e) => setField("reply_to", e.target.value)}
                />
                <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                  Where replies go. May be on any domain — unlike From, this is
                  never used to send, so it is not a spoofing surface.
                </div>
              </div>
            </div>
          )}

          {tab === "branding" && <ThemePanel companyId={company.id} />}

          {tab === "designer" && (
            <TemplatesPanel
              companyId={company.id}
              // The SAVED taxation, not the live form: the under-taxation
              // warning is about what this company actually issues under, and
              // an unsaved dropdown change is not that yet.
              companyTaxation={company.taxation}
            />
          )}

          {tab === "people" && <ContactsPanel companyId={company.id} />}

          {warnings.length > 0 && (
            <div className="stack-2" style={{ marginTop: 16 }}>
              {warnings.map((w) => (
                <div
                  key={w}
                  className="row gap-6"
                  style={{
                    color: "var(--warn)",
                    fontSize: 12,
                    alignItems: "center",
                  }}
                >
                  <I.AlertCircle size={12} />
                  <span>{w}</span>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div style={{ color: "var(--neg)", fontSize: 12, marginTop: 16 }}>
              {error}
            </div>
          )}

          <div
            className="row"
            style={{
              marginTop: 20,
              paddingTop: 16,
              borderTop: "1px solid var(--hair)",
              justifyContent: "flex-end",
            }}
          >
            <button
              className="btn accent"
              type="submit"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </form>
      <DocumentPreview
        companyId={company.id}
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  );
}
